import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { BACKEND_BASE_URL } from '../../utils/constants';
import LoadingIndicator from '../LoadingIndicator';

// Validate redirect URL to prevent open redirect vulnerabilities
// Moved outside component to avoid dependency array issues
const isValidRedirectUrl = (url) => {
  if (!url) return false;

  try {
    // Allow relative URLs
    if (url.startsWith('/')) return true;

    // For absolute URLs, check they match our domain
    const urlObj = new URL(url);
    const allowedDomains = [
      'minside.medarbeiderne.no',
      'app.medarbeiderne.no',
      'medarbeiderne.no',
      'localhost'
    ];

    return allowedDomains.some(domain =>
      urlObj.hostname === domain || urlObj.hostname.endsWith(`.${domain}`)
    );
  } catch (e) {
    // Invalid URL format
    return false;
  }
};

const RedirectShortenedComponent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [redirectUrl, setRedirectUrl] = useState(null);

  useEffect(() => {
    let timeoutId;
    let redirectTimeoutId;

    // Parse the query string to get the token
    const { token } = queryString.parse(location.search);

    if (!token) {
      console.error('No token provided in URL');
      setError('Ugyldig link - mangler token');
      timeoutId = setTimeout(() => navigate('/login'), 5000);
    } else {
      // Make a call to the API to get the new URL
      const apiUrl = `${BACKEND_BASE_URL}/shortenedurl?token=${token}`;

      if (process.env.NODE_ENV !== 'production') {
        console.log('Fetching redirect from:', apiUrl);
      }

      fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
          // Only send credentials for same-origin requests
          credentials: 'same-origin',
        })
        .then(response => {
          if (process.env.NODE_ENV !== 'production') {
            console.log('Response status:', response.status);
            console.log('Response headers:', response.headers);
          }

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          // Check content type before parsing
          const contentType = response.headers.get('content-type');
          if (!contentType || !contentType.includes('application/json')) {
            if (process.env.NODE_ENV !== 'production') {
              console.error('Unexpected content type:', contentType);
            }
            throw new Error(`Forventet JSON, fikk: ${contentType}`);
          }

          return response.json();
        })
        .then(data => {
          if (process.env.NODE_ENV !== 'production') {
            console.log('API response data:', data);
          }

          // Validate that we got a URL back
          if (!data || !data.url) {
            throw new Error('Ingen gyldig URL mottatt fra serveren');
          }

          const newUrl = data.url;

          // Validate redirect URL for security
          if (!isValidRedirectUrl(newUrl)) {
            throw new Error('Ugyldig omdirigerings-URL av sikkerhetsgrunner');
          }

          if (process.env.NODE_ENV !== 'production') {
            console.log('Redirecting to:', newUrl);
          }

          // Store URL for fallback link
          setRedirectUrl(newUrl);

          // Safari iOS fix: Add a small delay before redirect
          // Some Safari iOS versions may not handle immediate redirects correctly
          // from external sources (e.g. SMS links), so 100ms helps ensure the
          // redirect is processed properly without the user noticing.
          redirectTimeoutId = setTimeout(() => {
            window.location.replace(newUrl);
          }, 100);
        })
        .catch(error => {
          console.error('Error fetching redirect URL:', error);
          if (process.env.NODE_ENV !== 'production') {
            console.error('Error details:', { message: error.message, stack: error.stack });
          }
          setError(`Kunne ikke behandle lenken: ${error.message}`);
          // Redirect to login after 5 seconds
          timeoutId = setTimeout(() => navigate('/login'), 5000);
        });
    }

    // Cleanup timeouts on unmount to prevent memory leaks
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (redirectTimeoutId) {
        clearTimeout(redirectTimeoutId);
      }
    };
  }, [location.search, navigate]);

  if (error) {
    return (
      <div
        role="alert"
        aria-live="assertive"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '20px',
          textAlign: 'center'
        }}
      >
        <h2>Feil</h2>
        <p>{error}</p>
        <p style={{ fontSize: '14px', color: '#666', marginTop: '20px' }}>
          Du blir omdirigert til innlogging...
        </p>
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy={!redirectUrl}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '20px',
        textAlign: 'center'
      }}
    >
      <LoadingIndicator />
      <p style={{ marginTop: '20px', fontSize: '16px' }}>
        Omdirigerer...
      </p>
      {redirectUrl && (
        <p style={{ marginTop: '20px', fontSize: '14px', color: '#666' }}>
          Hvis du ikke blir omdirigert automatisk, <a href={redirectUrl} style={{ color: '#007bff', textDecoration: 'underline' }}>klikk her</a>.
        </p>
      )}
      <span style={{
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: 0,
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        border: 0
      }}>
        Laster... Vennligst vent mens vi omdirigerer deg.
      </span>
    </div>
  );
};

export default RedirectShortenedComponent;
